/**
 * Create Survey Page - Enhanced Multi-step wizard with Industry Templates
 * 
 * Step 0: Template Selection (NEW) - Choose industry template or start from scratch
 * Step 1: Basic Info - Survey name, purpose, frameworks
 * Step 2: Questions - Pre-populated from template or custom
 * Step 3: Recipients - Select suppliers
 * Step 4: Settings - Configuration options
 * 
 * Connected to backend APIs:
 * - GET /api/v1/industry-templates/industries
 * - POST /api/v1/industry-templates/create-survey
 * - POST /api/v1/supplier-portal/surveys
 * - GET /api/v1/supplier-portal/suppliers
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft,
  ChevronRight,
  Globe,
  ClipboardList,
  Users,
  Settings,
  Check,
  Plus,
  X,
  Search,
  Calendar,
  AlertCircle,
  Building2,
  Mail,
  FileText,
  HelpCircle,
  Trash2,
  Save,
  Send,
  Sparkles,
  Layers,
  TrendingUp,
  Clock,
  Star
} from 'lucide-react';
import IndustryTemplateSelector from '../../../components/supplier-portal/IndustryTemplateSelector';

const API = process.env.REACT_APP_BACKEND_URL;
import { apiFetch } from '../../../services/api_service';

const FRAMEWORKS = [
  { id: 'CSRD', label: 'CSRD / ESRS', description: 'Corporate Sustainability Reporting Directive' },
  { id: 'CDP', label: 'CDP Climate', description: 'Carbon Disclosure Project' },
  { id: 'GRI', label: 'GRI', description: 'Global Reporting Initiative' },
  { id: 'TCFD', label: 'TCFD', description: 'Task Force on Climate-related Financial Disclosures' },
  { id: 'SBTi', label: 'SBTi', description: 'Science Based Targets initiative' },
  { id: 'GHG', label: 'GHG Protocol', description: 'Greenhouse Gas Protocol' },
];

const QUESTION_TYPES = [
  { id: 'number', label: 'Number', icon: '123' },
  { id: 'text', label: 'Text', icon: 'Aa' },
  { id: 'select', label: 'Dropdown', icon: '▼' },
  { id: 'currency', label: 'Currency', icon: '$' },
  { id: 'date_range', label: 'Date Range', icon: '📅' },
  { id: 'file_upload', label: 'File Upload', icon: '📎' },
  { id: 'boolean', label: 'Yes/No', icon: '✓✗' },
  { id: 'multi_select', label: 'Multi-Select', icon: '☑' },
];

export default function CreateSurveyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const obligationId = searchParams.get('obligationId');
  
  const [currentStep, setCurrentStep] = useState(0); // Start at template selection
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [obligation, setObligation] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    surveyName: '',
    purpose: '',
    frameworks: [],
    dueDate: '',
    allowMultipleSubmissions: false,
    requireEvidence: true,
    autoReminders: true,
    reminderFrequency: 7,
    questions: [],
    templateId: null,
    industryId: null
  });

  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  useEffect(() => {
    if (obligationId) {
      fetchObligation(obligationId);
    }
  }, [obligationId]);

  useEffect(() => {
    if (currentStep === 3) {
      fetchSuppliers();
    }
  }, [currentStep]);

  const fetchObligation = async (id) => {
    try {
      const data = await apiFetch(`/obligations/${id}`);
      if (!data?._error) {
        setObligation(data);
        setFormData(prev => ({
          ...prev,
          surveyName: `${data.title || data.code || ''} - Supplier Data Collection`,
          purpose: data.description || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching obligation:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const data = await apiFetch('/supplier-portal/suppliers?limit=100');
      if (!data?._error) {
        setSuppliers(data.suppliers || []);
      }
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleTemplateSelect = (templateData) => {
    if (templateData) {
      setSelectedTemplate(templateData);
      setFormData(prev => ({
        ...prev,
        surveyName: `${templateData.template?.name || ''} - ${new Date().getFullYear()}`,
        purpose: templateData.template?.description || '',
        frameworks: templateData.frameworks || [],
        questions: templateData.template?.questions || [],
        templateId: templateData.template?.id,
        industryId: templateData.industry?.id
      }));
      setCurrentStep(1); // Move to Basic Info
    }
  };

  const handleSkipTemplate = () => {
    setSelectedTemplate(null);
    setCurrentStep(1); // Move to Basic Info without template
  };

  const handleCreateSurvey = async (shouldLaunch = false) => {
    try {
      setSaving(true);

      let surveyData;

      // If using a template, use the template endpoint
      if (formData.templateId) {
        surveyData = await apiFetch('/industry-templates/create-survey', {
          method: 'POST',
          body: {
            templateId: formData.templateId,
            surveyName: formData.surveyName,
            obligationId: obligationId || null,
            dueDate: formData.dueDate || null,
            supplierIds: selectedSuppliers,
            customizations: {
              purpose: formData.purpose,
              requireEvidence: formData.requireEvidence,
              allowMultipleSubmissions: formData.allowMultipleSubmissions,
              autoReminders: formData.autoReminders,
              reminderFrequency: formData.reminderFrequency
            }
          }
        });

        if (surveyData?._error) {
          throw new Error(surveyData.message || 'Failed to create survey from template');
        }
      } else {
        // Create survey directly
        surveyData = await apiFetch('/supplier-portal/surveys', {
          method: 'POST',
          body: {
            surveyName: formData.surveyName,
            purpose: formData.purpose,
            frameworks: formData.frameworks,
            dueDate: formData.dueDate || null,
            allowMultipleSubmissions: formData.allowMultipleSubmissions,
            requireEvidence: formData.requireEvidence,
            autoReminders: formData.autoReminders,
            reminderFrequency: formData.reminderFrequency,
            questions: formData.questions,
            obligationId: obligationId || null
          }
        });

        if (surveyData?._error) {
          throw new Error(surveyData.message || 'Failed to create survey');
        }

        const surveyId = surveyData.id;

        // Add recipients if any selected
        if (selectedSuppliers.length > 0) {
          await apiFetch(`/supplier-portal/surveys/${surveyId}/recipients`, {
            method: 'POST',
            body: { supplierIds: selectedSuppliers }
          });
        }

        // Launch if requested
        if (shouldLaunch && selectedSuppliers.length > 0) {
          await apiFetch(`/supplier-portal/surveys/${surveyId}/launch`, { method: 'POST' });
        }
      }

      const finalSurveyId = surveyData.survey?.id || surveyData.id;
      navigate(`/value-chain/supplier-portal/surveys/${finalSurveyId}`);
    } catch (err) {
      console.error('Error creating survey:', err);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { id: 0, label: 'Template', icon: Sparkles },
    { id: 1, label: 'Basic Info', icon: FileText },
    { id: 2, label: 'Questions', icon: ClipboardList },
    { id: 3, label: 'Recipients', icon: Users },
    { id: 4, label: 'Settings', icon: Settings },
  ];

  const isStepValid = (step) => {
    switch (step) {
      case 0:
        return true; // Template step is always valid (can skip)
      case 1:
        return formData.surveyName.trim() !== '' && formData.frameworks.length > 0;
      case 2:
        return formData.questions.length > 0;
      case 3:
        return true; // Optional
      case 4:
        return true;
      default:
        return false;
    }
  };

  const filteredSuppliers = suppliers.filter(s => 
    s.supplierName?.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    s.supplierId?.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="create-survey-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[#6B6B6B] text-sm">
        <Link to="/value-chain/supplier-portal" className="hover:text-purple-400 flex items-center gap-1">
          <Globe size={14} />
          <span>Supplier Portal</span>
        </Link>
        <ChevronRight size={14} />
        <Link to="/value-chain/supplier-portal/surveys" className="hover:text-purple-400">
          Surveys
        </Link>
        <ChevronRight size={14} />
        <span className="text-white">Create Survey</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Create New Survey</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {obligationId ? 'Create survey linked to obligation' : 'Collect ESG data from your suppliers'}
          </p>
        </div>
        
        {/* Template Badge */}
        {selectedTemplate && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 rounded-lg">
            <Sparkles size={14} className="text-purple-400" />
            <span className="text-sm text-purple-300">
              Using: {selectedTemplate.template?.name}
            </span>
          </div>
        )}
      </div>

      {/* Obligation Banner */}
      {obligation && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
          <Layers className="text-blue-400 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-blue-300 font-medium">
              Linked to Obligation: {obligation.code || obligation.obligationCode}
            </p>
            <p className="text-xs text-blue-200/70 mt-1">
              {obligation.title}
            </p>
          </div>
        </div>
      )}

      {/* Progress Steps */}
      <div className="flex items-center justify-center overflow-x-auto pb-2">
        {steps.map((step, idx) => (
          <React.Fragment key={step.id}>
            <button
              onClick={() => {
                if (step.id < currentStep || (isStepValid(currentStep) && step.id <= currentStep + 1)) {
                  setCurrentStep(step.id);
                }
              }}
              disabled={step.id > currentStep + 1}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors shrink-0 ${
                currentStep === step.id
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                  : step.id < currentStep
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-[#1F1F1F] text-[#6B6B6B]'
              } ${step.id > currentStep + 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {step.id < currentStep ? (
                <Check size={16} />
              ) : (
                <step.icon size={16} />
              )}
              <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
            </button>
            {idx < steps.length - 1 && (
              <div className={`w-8 h-0.5 mx-1 shrink-0 ${
                step.id < currentStep ? 'bg-emerald-500' : 'bg-[#2F2F2F]'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        {currentStep === 0 && (
          <TemplateSelectionStep 
            obligation={obligation}
            onSelectTemplate={handleTemplateSelect}
            onSkip={handleSkipTemplate}
          />
        )}

        {currentStep === 1 && (
          <BasicInfoStep 
            formData={formData} 
            setFormData={setFormData}
            selectedTemplate={selectedTemplate}
          />
        )}

        {currentStep === 2 && (
          <QuestionsStep 
            questions={formData.questions}
            setQuestions={(q) => setFormData({ ...formData, questions: q })}
            selectedTemplate={selectedTemplate}
          />
        )}

        {currentStep === 3 && (
          <RecipientsStep
            suppliers={filteredSuppliers}
            selectedSuppliers={selectedSuppliers}
            setSelectedSuppliers={setSelectedSuppliers}
            search={supplierSearch}
            setSearch={setSupplierSearch}
            loading={loadingSuppliers}
          />
        )}

        {currentStep === 4 && (
          <SettingsStep 
            formData={formData} 
            setFormData={setFormData}
            selectedSuppliers={selectedSuppliers}
            selectedTemplate={selectedTemplate}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            if (currentStep > 0) {
              setCurrentStep(currentStep - 1);
            } else {
              navigate('/value-chain/supplier-portal/surveys');
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg hover:bg-[#2F2F2F] transition-colors"
        >
          <ArrowLeft size={16} />
          {currentStep === 0 ? 'Cancel' : 'Previous'}
        </button>

        <div className="flex items-center gap-3">
          {currentStep === 4 ? (
            <>
              <button
                onClick={() => handleCreateSurvey(false)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-[#1F1F1F] text-[#A0A0A0] rounded-lg hover:bg-[#2F2F2F] transition-colors disabled:opacity-50"
              >
                <Save size={16} />
                Save as Draft
              </button>
              <button
                onClick={() => handleCreateSurvey(true)}
                disabled={saving || selectedSuppliers.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
              >
                <Send size={16} />
                {saving ? 'Creating...' : 'Create & Launch'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!isStepValid(currentStep)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors disabled:opacity-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Template Selection Step
function TemplateSelectionStep({ obligation, onSelectTemplate, onSkip }) {
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-2xl bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="text-purple-400" size={28} />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Start with a Template</h2>
        <p className="text-sm text-[#6B6B6B] max-w-lg mx-auto">
          Industry-specific templates include pre-built questions, expected outcomes, and framework mappings. 
          Choose a template to get started faster.
        </p>
      </div>

      <IndustryTemplateSelector 
        onSelectTemplate={onSelectTemplate}
        selectedObligation={obligation}
      />

      {/* Skip Option */}
      <div className="text-center pt-6 border-t border-[#1F1F1F]">
        <button
          onClick={onSkip}
          className="text-sm text-[#6B6B6B] hover:text-purple-400 transition-colors"
        >
          Or start from scratch →
        </button>
      </div>
    </div>
  );
}

// Basic Info Step
function BasicInfoStep({ formData, setFormData, selectedTemplate }) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-white">Survey Details</h2>

      {/* Template Info */}
      {selectedTemplate && (
        <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-start gap-3">
          <Sparkles className="text-purple-400 shrink-0 mt-0.5" size={18} />
          <div>
            <p className="text-sm text-purple-300 font-medium">
              Using template: {selectedTemplate.template?.name}
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-purple-200/70">
              <span className="flex items-center gap-1">
                <TrendingUp size={12} />
                {selectedTemplate.expectedOutcomes?.responseRate} response rate
              </span>
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {selectedTemplate.template?.estimatedTime}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm text-[#A0A0A0] mb-2">Survey Name *</label>
          <input
            type="text"
            value={formData.surveyName}
            onChange={(e) => setFormData({ ...formData, surveyName: e.target.value })}
            placeholder="e.g., 2025 Scope 3 Data Collection"
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white placeholder:text-[#6B6B6B] focus:border-purple-500/50 focus:outline-none"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm text-[#A0A0A0] mb-2">Purpose (optional)</label>
          <textarea
            value={formData.purpose}
            onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
            placeholder="Describe what this survey is for..."
            rows={3}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white placeholder:text-[#6B6B6B] focus:border-purple-500/50 focus:outline-none resize-none"
          />
        </div>

        <div>
          <label className="block text-sm text-[#A0A0A0] mb-2">Due Date (optional)</label>
          <input
            type="date"
            value={formData.dueDate}
            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-purple-500/50 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm text-[#A0A0A0] mb-3">Applicable Frameworks *</label>
        <div className="grid md:grid-cols-3 gap-3">
          {FRAMEWORKS.map((fw) => (
            <button
              key={fw.id}
              onClick={() => {
                const frameworks = formData.frameworks.includes(fw.id)
                  ? formData.frameworks.filter(f => f !== fw.id)
                  : [...formData.frameworks, fw.id];
                setFormData({ ...formData, frameworks });
              }}
              className={`p-4 rounded-lg border text-left transition-all ${
                formData.frameworks.includes(fw.id)
                  ? 'bg-purple-500/20 border-purple-500/50 text-purple-300'
                  : 'bg-[#0A0A0A] border-[#1F1F1F] text-[#A0A0A0] hover:border-purple-500/30'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-white">{fw.label}</span>
                {formData.frameworks.includes(fw.id) && (
                  <Check size={16} className="text-purple-400" />
                )}
              </div>
              <p className="text-xs text-[#6B6B6B]">{fw.description}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Questions Step
function QuestionsStep({ questions, setQuestions, selectedTemplate }) {
  const [editingQuestion, setEditingQuestion] = useState(null);

  const addQuestion = () => {
    const newQuestion = {
      questionId: `q${questions.length + 1}`,
      questionText: '',
      questionType: 'text',
      required: false,
      helpText: ''
    };
    setQuestions([...questions, newQuestion]);
    setEditingQuestion(questions.length);
  };

  const updateQuestion = (index, updates) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...updates };
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
    setEditingQuestion(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Survey Questions ({questions.length})</h2>
          {selectedTemplate && (
            <p className="text-xs text-[#6B6B6B] mt-1">
              Pre-populated from {selectedTemplate.template?.name} template
            </p>
          )}
        </div>
        <button
          onClick={addQuestion}
          className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm"
        >
          <Plus size={14} />
          Add Question
        </button>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
        {questions.map((q, idx) => (
          <div 
            key={q.questionId || idx}
            className={`p-4 rounded-lg border transition-all ${
              editingQuestion === idx 
                ? 'bg-[#0A0A0A] border-purple-500/50' 
                : 'bg-[#0A0A0A] border-[#1F1F1F] hover:border-[#2F2F2F]'
            }`}
          >
            {editingQuestion === idx ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Question Text</label>
                  <input
                    type="text"
                    value={q.questionText}
                    onChange={(e) => updateQuestion(idx, { questionText: e.target.value })}
                    placeholder="Enter your question..."
                    className="w-full px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-white text-sm focus:border-purple-500/50 focus:outline-none"
                    autoFocus
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-[#6B6B6B] mb-1">Type</label>
                    <select
                      value={q.questionType}
                      onChange={(e) => updateQuestion(idx, { questionType: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-white text-sm focus:border-purple-500/50 focus:outline-none"
                    >
                      {QUESTION_TYPES.map(type => (
                        <option key={type.id} value={type.id}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#6B6B6B] mb-1">Unit (optional)</label>
                    <input
                      type="text"
                      value={q.unit || ''}
                      onChange={(e) => updateQuestion(idx, { unit: e.target.value })}
                      placeholder="e.g., tCO2e"
                      className="w-full px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-white text-sm focus:border-purple-500/50 focus:outline-none"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={q.required}
                        onChange={(e) => updateQuestion(idx, { required: e.target.checked })}
                        className="w-4 h-4 rounded bg-[#1F1F1F] border-[#2F2F2F] text-purple-500 focus:ring-purple-500"
                      />
                      <span className="text-sm text-[#A0A0A0]">Required</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Help Text (optional)</label>
                  <input
                    type="text"
                    value={q.helpText || ''}
                    onChange={(e) => updateQuestion(idx, { helpText: e.target.value })}
                    placeholder="Additional guidance for suppliers..."
                    className="w-full px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-white text-sm focus:border-purple-500/50 focus:outline-none"
                  />
                </div>

                {/* Framework Mappings */}
                {q.frameworkMappings && (
                  <div className="flex flex-wrap gap-1">
                    {q.frameworkMappings.map((fw, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded">
                        {fw}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => removeQuestion(idx)}
                    className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setEditingQuestion(null)}
                    className="px-3 py-1.5 bg-emerald-500/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 text-sm"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="flex items-start gap-3 cursor-pointer"
                onClick={() => setEditingQuestion(idx)}
              >
                <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-xs font-bold shrink-0">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm text-white">{q.questionText || 'Click to edit...'}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#1F1F1F] text-[#6B6B6B]">{q.questionType}</span>
                    {q.required && <span className="text-[10px] text-amber-400">Required</span>}
                    {q.unit && <span className="text-[10px] text-[#6B6B6B]">{q.unit}</span>}
                    {q.frameworkMappings?.slice(0, 2).map((fw, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 bg-blue-500/10 text-blue-400 rounded">{fw}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {questions.length === 0 && (
        <div className="text-center py-12 bg-[#0A0A0A] rounded-lg border border-dashed border-[#2F2F2F]">
          <ClipboardList className="mx-auto text-[#2F2F2F] mb-4" size={48} />
          <p className="text-[#6B6B6B] mb-4">No questions added yet</p>
          <button
            onClick={addQuestion}
            className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30"
          >
            Add First Question
          </button>
        </div>
      )}
    </div>
  );
}

// Recipients Step
function RecipientsStep({ suppliers, selectedSuppliers, setSelectedSuppliers, search, setSearch, loading }) {
  const toggleSupplier = (supplierId) => {
    if (selectedSuppliers.includes(supplierId)) {
      setSelectedSuppliers(selectedSuppliers.filter(id => id !== supplierId));
    } else {
      setSelectedSuppliers([...selectedSuppliers, supplierId]);
    }
  };

  const toggleAll = () => {
    if (selectedSuppliers.length === suppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(suppliers.map(s => s.id));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Select Recipients ({selectedSuppliers.length})</h2>
        <button
          onClick={toggleAll}
          className="text-sm text-purple-400 hover:text-purple-300"
        >
          {selectedSuppliers.length === suppliers.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search suppliers..."
          className="w-full pl-10 pr-4 py-2.5 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-purple-500/50 focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      ) : suppliers.length === 0 ? (
        <div className="text-center py-12 bg-[#0A0A0A] rounded-lg border border-dashed border-[#2F2F2F]">
          <Building2 className="mx-auto text-[#2F2F2F] mb-4" size={48} />
          <p className="text-[#6B6B6B] mb-2">No suppliers found</p>
          <p className="text-xs text-[#6B6B6B]">Add suppliers to your database first</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
          {suppliers.map(supplier => (
            <button
              key={supplier.id}
              onClick={() => toggleSupplier(supplier.id)}
              className={`p-4 rounded-lg border text-left transition-all ${
                selectedSuppliers.includes(supplier.id)
                  ? 'bg-purple-500/20 border-purple-500/50'
                  : 'bg-[#0A0A0A] border-[#1F1F1F] hover:border-[#2F2F2F]'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                  selectedSuppliers.includes(supplier.id)
                    ? 'bg-purple-500 border-purple-500'
                    : 'border-[#2F2F2F]'
                }`}>
                  {selectedSuppliers.includes(supplier.id) && <Check size={12} className="text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{supplier.supplierName}</p>
                  <p className="text-xs text-[#6B6B6B] truncate">{supplier.primaryContact?.email || 'No email'}</p>
                  {supplier.tier && (
                    <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] bg-[#1F1F1F] text-[#6B6B6B]">
                      Tier {supplier.tier}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {selectedSuppliers.length === 0 && suppliers.length > 0 && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="text-amber-400 shrink-0" size={18} />
          <p className="text-sm text-amber-400">
            Select at least one supplier to launch the survey immediately, or save as draft.
          </p>
        </div>
      )}
    </div>
  );
}

// Settings Step
function SettingsStep({ formData, setFormData, selectedSuppliers, selectedTemplate }) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-white">Survey Settings</h2>

      {/* Summary */}
      <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
        <h3 className="text-sm font-medium text-purple-300 mb-3">Survey Summary</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-[#6B6B6B]">Name:</span>
            <span className="ml-2 text-white">{formData.surveyName}</span>
          </div>
          <div>
            <span className="text-[#6B6B6B]">Frameworks:</span>
            <span className="ml-2 text-white">{formData.frameworks.join(', ') || 'None'}</span>
          </div>
          <div>
            <span className="text-[#6B6B6B]">Questions:</span>
            <span className="ml-2 text-white">{formData.questions.length}</span>
          </div>
          <div>
            <span className="text-[#6B6B6B]">Recipients:</span>
            <span className="ml-2 text-white">{selectedSuppliers.length}</span>
          </div>
          {selectedTemplate && (
            <div className="col-span-2">
              <span className="text-[#6B6B6B]">Template:</span>
              <span className="ml-2 text-purple-300">{selectedTemplate.template?.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Expected Outcomes */}
      {selectedTemplate?.expectedOutcomes && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <h3 className="text-sm font-medium text-emerald-300 mb-3 flex items-center gap-2">
            <Star size={14} />
            Expected Outcomes (Based on Industry Data)
          </h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs text-emerald-200/70">Response Rate</p>
              <p className="text-emerald-300 font-medium">{selectedTemplate.expectedOutcomes.responseRate}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-200/70">Completion Time</p>
              <p className="text-emerald-300 font-medium">{selectedTemplate.expectedOutcomes.completionTime}</p>
            </div>
            <div>
              <p className="text-xs text-emerald-200/70">Data Quality</p>
              <p className="text-emerald-300 font-medium">{selectedTemplate.expectedOutcomes.dataQualityScore}</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="space-y-4">
        <label className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] cursor-pointer">
          <div>
            <p className="text-sm text-white">Require Evidence</p>
            <p className="text-xs text-[#6B6B6B]">Suppliers must upload supporting documents</p>
          </div>
          <input
            type="checkbox"
            checked={formData.requireEvidence}
            onChange={(e) => setFormData({ ...formData, requireEvidence: e.target.checked })}
            className="w-5 h-5 rounded bg-[#1F1F1F] border-[#2F2F2F] text-purple-500 focus:ring-purple-500"
          />
        </label>

        <label className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] cursor-pointer">
          <div>
            <p className="text-sm text-white">Allow Multiple Submissions</p>
            <p className="text-xs text-[#6B6B6B]">Suppliers can submit revised responses</p>
          </div>
          <input
            type="checkbox"
            checked={formData.allowMultipleSubmissions}
            onChange={(e) => setFormData({ ...formData, allowMultipleSubmissions: e.target.checked })}
            className="w-5 h-5 rounded bg-[#1F1F1F] border-[#2F2F2F] text-purple-500 focus:ring-purple-500"
          />
        </label>

        <label className="flex items-center justify-between p-4 bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] cursor-pointer">
          <div>
            <p className="text-sm text-white">Automatic Reminders</p>
            <p className="text-xs text-[#6B6B6B]">Send periodic reminders to non-responders</p>
          </div>
          <input
            type="checkbox"
            checked={formData.autoReminders}
            onChange={(e) => setFormData({ ...formData, autoReminders: e.target.checked })}
            className="w-5 h-5 rounded bg-[#1F1F1F] border-[#2F2F2F] text-purple-500 focus:ring-purple-500"
          />
        </label>

        {formData.autoReminders && (
          <div className="pl-4">
            <label className="block text-sm text-[#A0A0A0] mb-2">Reminder Frequency (days)</label>
            <select
              value={formData.reminderFrequency}
              onChange={(e) => setFormData({ ...formData, reminderFrequency: parseInt(e.target.value) })}
              className="w-32 px-3 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white text-sm focus:border-purple-500/50 focus:outline-none"
            >
              <option value={3}>Every 3 days</option>
              <option value={7}>Every 7 days</option>
              <option value={14}>Every 14 days</option>
              <option value={30}>Every 30 days</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
